import moment from "moment";
import { create } from "zustand";

interface Slot {
  start: string;
  end: string;
}

interface BookingState {
  selectedDate: moment.Moment;
  selectedSlots: Slot[];
  totalDuration: number;
  totalPrice: number;
  userBookingAllData: UserBookingAllData | null;
  currentBayValue: number;
  customerName: string | null | number;
  playersCount: number;
  customerList: Customer[] | null;
  minStartTime: string | null;
  maxEndTime: string | null;
  availableBays: number[] | null;
  selectedBays: number[];
  selectedStartTime: string | null;
  selectedEndTime: string | null;
  editBooking: Booking | null;
  isSelectingTime: boolean;
  showInlineCustomerModal: boolean;

  setSelectedDate: (date: moment.Moment) => void;
  addSlot: (slot: Slot, price: number) => void;
  removeSlot: (slot: Slot, price: number) => void;
  setUserBookingAllData: (data: UserBookingAllData) => void;
  setCurrentBayValue: (bayValue: number) => void;
  resetSelectedSlots: () => void;
  setCustomerName: (name: number | string | null) => void;
  setPlayersCount: (count: number) => void;
  setCustomerList: (list: Customer[]) => void;
  updateTimeRange: () => void;
  setAvailableBays: (bays: number[]) => void;
  setSelectedBays: (bays: number[]) => void;
  setSelectedStartTime: (time: string | null) => void;
  setSelectedEndTime: (time: string | null) => void;
  setEditBooking: (booking: Booking | null) => void;
  setIsSelectingTime: (isSelectingTime: boolean) => void;
  setTotalDuration: (duration: number) => void;
  setShowInlineCustomerModal: (show: boolean) => void;
}

const useBookingStore = create<BookingState>((set, get) => ({
  selectedDate: moment(),
  selectedSlots: [],
  totalDuration: 0,
  totalPrice: 0,
  userBookingAllData: null,
  currentBayValue: 1,
  playersCount: 1,
  customerName: null,
  customerList: null,
  minStartTime: null,
  maxEndTime: null,
  availableBays: null,
  selectedBays: [],
  selectedStartTime: null,
  selectedEndTime: null,
  editBooking: null,
  isSelectingTime: false,
  showInlineCustomerModal: false,

  setIsSelectingTime: (isSelectingTime) => set({ isSelectingTime }),
  setShowInlineCustomerModal: (show) => set({ showInlineCustomerModal: show }),

  setSelectedDate: (date) => set({ selectedDate: date }),
  addSlot: (slot, price) => {
    const updatedSlots = [...get().selectedSlots, slot];
    set({
      selectedSlots: updatedSlots,
      totalDuration: get().totalDuration + 30,
      totalPrice: get().totalPrice + price,
    });
    get().updateTimeRange();
  },
  removeSlot: (slot, price) => {
    const updatedSlots = get().selectedSlots.filter(
      (s) => !(s.start === slot.start && s.end === slot.end)
    );
    set({
      selectedSlots: updatedSlots,
      totalDuration: get().totalDuration - 30,
      totalPrice: get().totalPrice - price,
    });
    get().updateTimeRange();
  },
  updateTimeRange: () => {
    const slots = get().selectedSlots;
    const minStartTime = slots.length
      ? slots.reduce(
          (min, slot) =>
            moment(slot.start, "hh:mm A").isBefore(moment(min, "hh:mm A"))
              ? slot.start
              : min,
          slots[0].start
        )
      : null;

    const maxEndTime = slots.length
      ? slots.reduce(
          (max, slot) =>
            moment(slot.end, "hh:mm A").isAfter(moment(max, "hh:mm A"))
              ? slot.end
              : max,
          slots[0].end
        )
      : null;

    // Also update selectedStartTime and selectedEndTime for display
    set({
      minStartTime,
      maxEndTime,
      selectedStartTime: minStartTime,
      selectedEndTime: maxEndTime
    });
  },
  setUserBookingAllData: (data) => set({ userBookingAllData: data }),
  setCurrentBayValue: (bayValue) => set({ currentBayValue: bayValue }),
  resetSelectedSlots: () =>
    set({
      selectedSlots: [],
      totalDuration: 0,
      totalPrice: 0,
      minStartTime: null,
      maxEndTime: null,
      selectedStartTime: null,
      selectedEndTime: null,
    }),
  setCustomerName: (name) => set({ customerName: name }),
  setPlayersCount: (count) => set({ playersCount: count }),
  setCustomerList: (list) => set({ customerList: list }),
  setAvailableBays: (bays) => set({ availableBays: bays }),
  setSelectedBays: (bays) => set({ selectedBays: bays }),
  setSelectedStartTime: (time) => set({ selectedStartTime: time }),
  setSelectedEndTime: (time) => set({ selectedEndTime: time }),
  setEditBooking: (booking) =>
    set({
      editBooking: booking,
    }),
  setTotalDuration: (duration) => set({ totalDuration: duration }),
}));
export default useBookingStore;
